// src/App.jsx — Composition root for Carbon Insight Tool.
import React, { useEffect } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import {
  AuthProvider,
  getAuthenticatedUserId,
  useAuthContext,
} from './context/AuthContext';
import { ThemeProvider, useThemeContext } from './context/ThemeContext';
import { RequireRole } from './auth';
import { ROUTES } from './config/routes';
import { useMuiTheme } from './theme/muiTheme';
import { FORBIDDEN_EVENT } from './shared/api';

import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';

import Forbidden403Page from './pages/Forbidden403Page';
import LoginScreen from './features/auth/components/LoginScreen';

import './styles.css';
function MuiThemeWrapper({ children }) {
  const theme = useMuiTheme();
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </MuiThemeProvider>
  );
}

function AuthenticatedApp() {
  return (
    <>
      <AppLayout>
        <Routes>
          {ROUTES.map(({ path, element, roles }) => (
            <Route
              key={path}
              path={path}
              element={
                roles?.length
                  ? <RequireRole roles={roles}>{element}</RequireRole>
                  : element
              }
            />
          ))}
          <Route path="/403" element={<Forbidden403Page />} />
          <Route path="/"   element={<Navigate to="/overview" replace />} />
          <Route path="*"   element={<Navigate to="/overview" replace />} />
        </Routes>
      </AppLayout>
    </>
  );
}

// Applies the active theme class while routing authenticated and public screens.
function AppRoutes() {
  const { auth, login, sessionExpired } = useAuthContext();
  const { appClass } = useThemeContext();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = t('breadcrumb.appName');
  }, [i18n.language, t]);

  useEffect(() => {
    const showPermissionPage = () => navigate('/403', { replace: true });
    window.addEventListener(FORBIDDEN_EVENT, showPermissionPage);
    return () => window.removeEventListener(FORBIDDEN_EVENT, showPermissionPage);
  }, [navigate]);

  return (
    <div className={appClass}>
      <Routes>
        <Route
          path="/login"
          element={
            auth
              ? <Navigate to="/overview" replace />
              : <AuthLayout>
                  <LoginScreen
                    onLogin={data => login(data)}
                    sessionExpired={sessionExpired}
                  />
                </AuthLayout>
          }
        />
        <Route
          path="/*"
          element={!auth
            ? <Navigate to="/login" replace />
            : <AuthenticatedApp key={getAuthenticatedUserId(auth)} />}
        />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <MuiThemeWrapper>
            <AppRoutes />
          </MuiThemeWrapper>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
